import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { IAlert } from '../../../alert-kinds/index.js';
import { useClientApis } from '../../hooks/ClientApisProvider.js';
import { alertsKeys, newAlertArrivals, patchAlerts, type IAlertsState } from '../alerts.js';

const NO_ALERTS: IAlert[] = [];

/** The site's alerts list as the stream has built it — `undefined` until its first frame. */
function useAlertsState(siteId: string | null): IAlertsState | undefined {
  // Nothing to fetch: the stream writes this entry (`receiveAlertsFrame`).
  return useQuery<IAlertsState>({ queryKey: alertsKeys.list(siteId ?? ''), queryFn: skipToken }).data;
}

/** The person's alerts in the site, newest first; empty until the stream has said. */
export function useAlerts(siteId: string | null): IAlert[] {
  return useAlertsState(siteId)?.alerts ?? NO_ALERTS;
}

export interface IAlertArrivals {
  /** Moves once per frame that brought alerts not listed before. */
  count: number;
  arrived: readonly IAlert[];
}

/** The site's alert arrivals — what a sound, a swing or a peek answers to.
 *  The first list and every reset are baselines (`newAlertArrivals`). */
export function useAlertArrivals(siteId: string | null): IAlertArrivals {
  const state = useAlertsState(siteId);
  const [arrivals, setArrivals] = useState<IAlertArrivals>({ count: 0, arrived: [] });
  const previous = useRef<{ siteId: string | null; state: IAlertsState } | null>(null);

  useEffect(() => {
    if (!state) return;
    const before = previous.current;
    previous.current = { siteId, state };
    if (!before || before.siteId !== siteId) return;
    const arrived = newAlertArrivals(before.state, state);
    if (arrived.length > 0) setArrivals(({ count }) => ({ count: count + 1, arrived }));
  }, [siteId, state]);

  return arrivals;
}

export interface IAlertActions {
  markViewed(alertId: string): void;
  dismiss(alertId: string): void;
  markAllViewed(): void;
}

/**
 * What the person does to their alerts. Each change shows at once and is
 * sent to the server; if the server refuses, the list is put back. The
 * person's other tabs and devices take the engine's list afresh on the
 * server's signal.
 */
export function useAlertActions(siteId: string | null): IAlertActions {
  const queryClient = useQueryClient();
  const { markAlertViewed, dismissAlert, markAllAlertsViewed } = useClientApis().notifications;

  const apply = useCallback(
    (change: (alert: IAlert) => IAlert | null, send: (siteId: string) => Promise<void>) => {
      if (!siteId) return;
      const key = alertsKeys.list(siteId);
      const before = queryClient.getQueryData<IAlertsState>(key);
      queryClient.setQueryData<IAlertsState | undefined>(key, (state) => patchAlerts(state, change));
      send(siteId).catch(() => queryClient.setQueryData(key, before));
    },
    [queryClient, siteId],
  );

  return useMemo(
    () => ({
      markViewed: (alertId) =>
        apply((a) => (a.id === alertId ? { ...a, viewed: true } : a), (s) => markAlertViewed(s, alertId)),
      dismiss: (alertId) => apply((a) => (a.id === alertId ? null : a), (s) => dismissAlert(s, alertId)),
      markAllViewed: () => apply((a) => ({ ...a, viewed: true }), (s) => markAllAlertsViewed(s)),
    }),
    [apply, markAlertViewed, dismissAlert, markAllAlertsViewed],
  );
}
